import { cn } from "@/lib/utils";
import { CheckCircle2, Clock, X } from "lucide-react";

import { SubStep, ConditionalStep } from "@/types/wizard";

interface ProgressIndicatorProps {
  steps: string[];
  currentStep: number;
  currentSubSteps?: SubStep[];
  conditionalSteps?: ConditionalStep[];
  conditionalStepSubSteps?: SubStep[];
  onStepClick?: (stepIndex: number) => void;
  className?: string;
  firstStepClickable?: boolean;
  stepSublabels?: readonly (string | undefined)[];
}

export function ProgressIndicator({
  steps = [],
  currentStep,
  currentSubSteps,
  conditionalSteps,
  conditionalStepSubSteps,
  onStepClick,
  className,
  firstStepClickable = false,
  stepSublabels,
}: ProgressIndicatorProps) {
  // Safety check to prevent errors when steps is undefined
  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <div className={cn("w-full max-w-6xl mx-auto", className)}>
      {/* Main steps */}
      <div className="bg-card rounded-lg p-2">
        <div className="overflow-x-auto">
          <div className="flex min-w-max justify-center gap-0">
            {steps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              const isUpcoming = index > currentStep;
              const isFirstStep = index === 0;
              const canClickBack =
                isCompleted &&
                !!onStepClick &&
                (!isFirstStep || firstStepClickable);
              const sublabel = stepSublabels?.[index];

              return (
                <div key={step} className="flex items-center flex-shrink-0">
                  {/* Step Circle */}
                  <div className="flex flex-col items-center w-20 sm:w-28">
                    <div
                      className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center border-2 transition-smooth shadow-sm",
                        {
                          "bg-success text-white border-success hover:bg-success/90 cursor-pointer":
                            canClickBack,
                          "bg-success text-white border-success cursor-default":
                            isCompleted && !canClickBack,
                          "bg-primary text-primary-foreground border-primary shadow-button":
                            isCurrent,
                          "border-border bg-muted text-muted-foreground":
                            isUpcoming,
                        }
                      )}
                      onClick={
                        canClickBack ? () => onStepClick(index) : undefined
                      }
                      role={canClickBack ? "button" : undefined}
                      aria-label={canClickBack ? `Go to ${step}` : undefined}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <span className="text-xs font-bold">{index + 1}</span>
                      )}
                    </div>

                    {/* Step Label */}
                    <span
                      className={cn(
                        "mt-1 text-ds-xxs font-medium text-center max-w-20 sm:max-w-28 leading-tight transition-smooth",
                        {
                          "text-success hover:text-success/90 cursor-pointer":
                            canClickBack,
                          "text-success cursor-default":
                            isCompleted && !canClickBack,
                          "text-primary font-semibold": isCurrent,
                          "text-muted-foreground": isUpcoming,
                        }
                      )}
                      onClick={
                        canClickBack ? () => onStepClick(index) : undefined
                      }
                      role={canClickBack ? "button" : undefined}
                    >
                      {step}
                    </span>

                    {sublabel && (
                      <span className="mt-0.5 text-[8px] text-muted-foreground text-center max-w-20 sm:max-w-28 leading-tight">
                        {sublabel}
                      </span>
                    )}
                  </div>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "w-8 sm:w-12 h-1 mx-2 rounded-full transition-smooth mt-3 flex-shrink-0",
                        {
                          "bg-success": isCompleted,
                          "bg-primary/30": isCurrent,
                          "bg-muted": isUpcoming,
                        }
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Conditional steps */}
      {conditionalSteps && conditionalSteps.length > 0 && (
        <div className="bg-card rounded-lg p-4 sm:p-6 shadow-card mb-6">
          <div className="overflow-x-auto">
            <div className="flex justify-center min-w-max">
              {conditionalSteps.map((conditionalStep, index) => (
                <div
                  key={conditionalStep.id}
                  className="flex items-center flex-shrink-0 mx-2"
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center border-2 transition-smooth shadow-sm",
                        {
                          "bg-primary text-primary-foreground border-primary shadow-button":
                            conditionalStep.status === "active",
                          "border-warning bg-warning/10 text-warning":
                            conditionalStep.status === "inactive",
                        }
                      )}
                    >
                      {conditionalStep.status === "active" ? (
                        <Clock className="w-6 h-6" />
                      ) : (
                        <span className="text-sm font-bold">!</span>
                      )}
                    </div>

                    <span
                      className={cn(
                        "mt-3 text-sm font-medium text-center max-w-24 sm:max-w-32 leading-tight transition-smooth",
                        {
                          "text-primary font-semibold":
                            conditionalStep.status === "active",
                          "text-warning": conditionalStep.status === "inactive",
                        }
                      )}
                    >
                      {conditionalStep.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
